import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { SavedItemType } from "@corianpark/shared";
import { useAuth } from "../hooks/useAuth.js";
import {
  useSaveItem,
  useSavedList,
  useUnsaveItem,
  useUpdateSavedNote,
} from "../hooks/useSaved.js";

interface SaveButtonProps {
  itemType: SavedItemType;
  itemId: string;
  size?: "small" | "medium";
  variant?: "overlay" | "inline";
}

export function SaveButton({ itemType, itemId, size = "small", variant = "overlay" }: SaveButtonProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data } = useSavedList();
  const saveMut = useSaveItem();
  const unsaveMut = useUnsaveItem();
  const noteMut = useUpdateSavedNote();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draftNote, setDraftNote] = useState("");

  const existing = data?.items.find((s) => s.itemType === itemType && s.itemId === itemId);
  const isSaved = !!existing;
  const pending = saveMut.isPending || unsaveMut.isPending;

  // When the popover opens, hydrate the draft from the live note (so editing
  // an existing note shows what's there).
  useEffect(() => {
    if (anchor && existing) setDraftNote(existing.note ?? "");
  }, [anchor, existing]);

  const handle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) {
      navigate("/auth/sign-in");
      return;
    }
    if (existing) {
      // Already saved → open the note popover anchored at the button.
      setAnchor(e.currentTarget);
    } else {
      saveMut.mutate({ itemType, itemId });
    }
  };

  const closePopover = () => setAnchor(null);

  const overlayStyles = variant === "overlay"
    ? {
        position: "absolute" as const,
        top: 8,
        right: 8,
        bgcolor: "rgba(0,0,0,0.6)",
        color: isSaved ? "primary.main" : "common.white",
        backdropFilter: "blur(4px)",
        zIndex: 2,
        "&:hover": { bgcolor: "rgba(0,0,0,0.8)", color: "primary.main" },
      }
    : {
        color: isSaved ? "primary.main" : "text.secondary",
        "&:hover": { color: "primary.main" },
      };

  return (
    <>
      <Tooltip title={isSaved ? "In your Loader Bucket — click to edit note or remove" : "Save to Loader Bucket"}>
        <IconButton
          size={size}
          disabled={pending}
          onClick={handle}
          aria-label={isSaved ? "Edit saved item" : "Save"}
          sx={overlayStyles}
        >
          {isSaved ? <BookmarkIcon fontSize={size} /> : <BookmarkBorderIcon fontSize={size} />}
        </IconButton>
      </Tooltip>

      {existing && (
        <Popover
          open={!!anchor}
          anchorEl={anchor}
          onClose={closePopover}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{
            paper: {
              onClick: (e: React.MouseEvent) => e.stopPropagation(),
              sx: { p: 2, width: 320, maxWidth: "calc(100vw - 32px)" },
            },
          }}
        >
          <Stack spacing={1.5}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Your note
            </Typography>
            <TextField
              multiline
              minRows={3}
              maxRows={6}
              placeholder="Why does this matter? (optional)"
              value={draftNote}
              onChange={(e) => setDraftNote(e.target.value.slice(0, 500))}
              fullWidth
              autoFocus
              size="small"
              helperText={`${draftNote.length}/500`}
            />
            <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutlinedIcon fontSize="small" />}
                onClick={() => {
                  unsaveMut.mutate(existing.id);
                  closePopover();
                }}
                disabled={unsaveMut.isPending}
              >
                Remove
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={closePopover}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={noteMut.isPending || draftNote === (existing.note ?? "")}
                onClick={() => {
                  noteMut.mutate(
                    { id: existing.id, note: draftNote || null },
                    { onSuccess: closePopover },
                  );
                }}
              >
                {noteMut.isPending ? "Saving…" : "Save note"}
              </Button>
            </Stack>
          </Stack>
        </Popover>
      )}
    </>
  );
}
